/**
 * CKC Analytics — thin wrapper around the analytics vendor (currently the
 * Amplitude Browser SDK v2, loaded from the CDN by `snippets/ckc-analytics.liquid`).
 *
 * ALL theme code must call `window.CKCAnalytics` (or dispatch through this
 * file), never the vendor global directly, so swapping vendors (e.g. PostHog)
 * touches only this file + the snippet.
 *
 * Identity: we identify by the signed-in account email (lowercased) via
 * CKCAuth.onChange — the same user_id the mobile app uses, so web + app
 * sessions stitch into one Amplitude user. reset() only fires on a real
 * sign-out transition (never on an anonymous page load) so anonymous device
 * ids stay stable.
 */
(() => {
  'use strict';

  const cfg = window.CKC_ANALYTICS_CONFIG || {};
  const KEY = cfg.amplitudeApiKey || '';

  // Public no-op surface so callers never need to feature-detect.
  const api = {
    track: (_name, _props) => {},
    identify: (_userId) => {},
    reset: () => {},
  };
  window.CKCAnalytics = api;
  if (!KEY || !window.amplitude) return;

  const amp = window.amplitude;

  // Session replay ships in the same CDN bundle; sample 100% — traffic is far
  // below the free-tier replay quota, and replays are where funnel drop-off
  // reasons actually become visible.
  try {
    if (window.sessionReplay && typeof window.sessionReplay.plugin === 'function') {
      amp.add(window.sessionReplay.plugin({ sampleRate: 1 }));
    }
  } catch (e) {
    /* replay is optional — never block analytics */
  }

  // Adopt the device id the loader snippet established synchronously in the
  // head (and wrote to the `ckc_amp_did` cookie the Shopify pixel reads), so
  // the SDK and the pixel agree BY CONSTRUCTION rather than by the SDK
  // publishing its id afterwards and hoping the pixel hadn't already fired.
  // Returning visitors carry the id the SDK itself generated on an earlier
  // visit, so pinning it changes nothing for them — no user is split.
  const initResult = amp.init(
    KEY,
    Object.assign(
      { autocapture: true }, // page views, sessions, form + element interactions
      cfg.deviceId ? { deviceId: cfg.deviceId } : {} // absent only if cookies are blocked
    )
  );

  // Session id can only be published after init (the SDK owns it). The device
  // id is re-published defensively: normally a no-op, but it repairs the
  // cookie if the head snippet couldn't write it. Short TTL on the session id
  // roughly matches Amplitude's 30-min session timeout; it refreshes per page.
  function publishIds() {
    try {
      const did = amp.getDeviceId && amp.getDeviceId();
      const sid = amp.getSessionId && amp.getSessionId();
      if (did) {
        document.cookie = 'ckc_amp_did=' + encodeURIComponent(did) + '; path=/; max-age=31536000; SameSite=Lax';
      }
      if (sid) {
        document.cookie = 'ckc_amp_sid=' + sid + '; path=/; max-age=1800; SameSite=Lax';
      }
    } catch (e) {
      /* ignore */
    }
  }
  if (initResult && initResult.promise && typeof initResult.promise.then === 'function') {
    initResult.promise.then(publishIds);
  } else {
    publishIds();
  }

  api.track = (name, props) => {
    try {
      amp.track(name, Object.assign({ platform_surface: 'web' }, props || {}));
    } catch (e) {
      /* analytics must never break the storefront */
    }
  };
  api.identify = (userId) => {
    try {
      if (userId) amp.setUserId(String(userId).toLowerCase());
    } catch (e) {
      /* ignore */
    }
  };
  api.reset = () => {
    try {
      amp.reset();
    } catch (e) {
      /* ignore */
    }
  };

  // ---- identity: follow the custom-auth session ----
  // custom-auth.js may load after us; poll briefly for its global.
  let wasSignedIn = false;
  function bindAuth() {
    if (!window.CKCAuth || typeof window.CKCAuth.onChange !== 'function') return false;
    window.CKCAuth.onChange((user, ready) => {
      if (user && user.email) {
        wasSignedIn = true;
        api.identify(user.email);
      } else if (ready && wasSignedIn) {
        // real sign-out transition only — keeps anonymous device ids stable
        wasSignedIn = false;
        api.reset();
      }
    });
    return true;
  }
  if (!bindAuth()) {
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (bindAuth() || tries > 40) clearInterval(timer); // give up after ~10s
    }, 250);
  }
})();
